// =============================================================================
// VandieZ admin-web -- GlobalSearch
// =============================================================================
// Sidebar search input + dropdown. 250 ms debounce, hits
// GET /admin/search?q=... which returns up to 5 results per resource
// group (users, trips, tickets, vehicles). Clicking a result navigates
// to the per-resource page and closes the dropdown.
//
// Cmd/Ctrl+K focuses the input from anywhere on the page.
// Esc closes the dropdown.
// =============================================================================

import {
  ClipboardCheck,
  LifeBuoy,
  Loader2,
  Package,
  Search,
  Truck,
  Users,
  X,
} from "lucide-react";
import type React from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { adminSearchApi, type AdminSearchResult } from "@/api/client";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/lib/auth";
import { cn } from "@/lib/utils";

const DEBOUNCE_MS = 250;

const EMPTY: AdminSearchResult = {
  users: [],
  trips: [],
  tickets: [],
  vehicles: [],
  loads: [],
};

// Flattens the grouped result into the order the dropdown renders them
// so ArrowDown/ArrowUp index maps 1:1 to the visible rows.
interface FlatHit {
  path: string;
}

function flattenResults(r: AdminSearchResult): FlatHit[] {
  return [
    ...r.users.map((u) => ({ path: `/users/${u.id}` })),
    ...r.trips.map((t) => ({ path: `/trips/${t.id}` })),
    ...r.loads.map((l) => ({ path: `/loads/${l.id}` })),
    ...r.tickets.map((t) => ({ path: `/tickets/${t.id}` })),
    ...r.vehicles.map((v) => ({ path: `/vehicles/${v.id}` })),
  ];
}

export function GlobalSearch(): React.JSX.Element {
  const { accessToken } = useAuth();
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const [query, setQuery] = useState<string>("");
  const [results, setResults] = useState<AdminSearchResult>(EMPTY);
  const [loading, setLoading] = useState<boolean>(false);
  const [open, setOpen] = useState<boolean>(false);
  // Highlighted result index for arrow-key navigation. -1 = nothing
  // highlighted (Enter goes nowhere). Reset on every query/result
  // change so the user doesn't jump to a stale row.
  const [highlightIdx, setHighlightIdx] = useState<number>(-1);

  // Debounced fetch.
  useEffect(() => {
    if (accessToken === null) {return undefined;}
    const trimmed = query.trim();
    if (trimmed.length < 2) {
      setResults(EMPTY);
      setLoading(false);
      return undefined;
    }
    const controller = new AbortController();
    setLoading(true);
    const handle = setTimeout(() => {
      adminSearchApi
        .search(accessToken, trimmed, controller.signal)
        .then((res) => {
          setResults(res);
          // Auto-highlight the first result so Enter is immediately useful.
          const hits = flattenResults(res);
          setHighlightIdx(hits.length > 0 ? 0 : -1);
        })
        .catch((e: unknown) => {
          if (e instanceof Error && e.name === "AbortError") {return;}
          setResults(EMPTY);
          setHighlightIdx(-1);
        })
        .finally(() => { setLoading(false); });
    }, DEBOUNCE_MS);
    return () => {
      controller.abort();
      clearTimeout(handle);
    };
  }, [accessToken, query]);

  // Cmd/Ctrl + K focuses the search bar.
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        inputRef.current?.focus();
      } else if (e.key === "Escape") {
        setOpen(false);
        inputRef.current?.blur();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("keydown", onKey); };
  }, []);

  // Click-outside to close the dropdown.
  useEffect(() => {
    if (!open) {return undefined;}
    const onClick = (e: MouseEvent): void => {
      if (
        containerRef.current !== null &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => { document.removeEventListener("mousedown", onClick); };
  }, [open]);

  const go = useCallback(
    (path: string): void => {
      navigate(path);
      setOpen(false);
      setQuery("");
    },
    [navigate],
  );

  const flatHits = flattenResults(results);
  const totalHits = flatHits.length;
  const trimmed = query.trim();
  const showDropdown = open && trimmed.length >= 2;

  const onInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (!showDropdown || totalHits === 0) {return;}
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightIdx((i) => (i + 1) % totalHits);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightIdx((i) => (i - 1 + totalHits) % totalHits);
    } else if (e.key === "Enter") {
      e.preventDefault();
      const target = flatHits[highlightIdx];
      if (target !== undefined) {
        go(target.path);
      }
    }
  };

  // Build a flat index -> visual highlight map so each Row knows whether
  // it's the active one. The order mirrors flattenResults.
  let cursor = -1;
  const isHighlighted = (
    kind: "users" | "trips" | "loads" | "tickets" | "vehicles",
    localIdx: number,
  ): boolean => {
    let groupStart = 0;
    if (kind === "trips") {groupStart = results.users.length;}
    else if (kind === "loads") {
      groupStart = results.users.length + results.trips.length;
    }
    else if (kind === "tickets") {
      groupStart =
        results.users.length + results.trips.length + results.loads.length;
    }
    else if (kind === "vehicles") {
      groupStart =
        results.users.length + results.trips.length +
        results.loads.length + results.tickets.length;
    }
    return groupStart + localIdx === highlightIdx;
  };
  void cursor;

  return (
    <div ref={containerRef} className="relative">
      <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
      <Input
        ref={inputRef}
        type="search"
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
        }}
        onFocus={() => { setOpen(true); }}
        onKeyDown={onInputKeyDown}
        placeholder="Search (Ctrl+K)"
        className="pl-7 pr-7 h-9"
        aria-label="Global search"
      />
      {query.length > 0 && (
        <button
          type="button"
          aria-label="Clear search"
          className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
          onClick={() => { setQuery(""); setOpen(false); }}
        >
          <X className="h-3.5 w-3.5" />
        </button>
      )}

      {showDropdown && (
        <div
          className={cn(
            "absolute left-0 right-0 mt-1 z-50 rounded-md border bg-popover shadow-lg",
            "max-h-[60vh] overflow-y-auto",
          )}
          role="listbox"
        >
          {loading && (
            <div className="flex items-center gap-2 px-3 py-3 text-sm text-muted-foreground">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              Searching…
            </div>
          )}

          {!loading && totalHits === 0 && (
            <div className="px-3 py-3 text-sm text-muted-foreground">
              No matches for &quot;{trimmed}&quot;.
            </div>
          )}

          {results.users.length > 0 && (
            <Group title="Users" icon={Users}>
              {results.users.map((u, i) => (
                <Row
                  key={u.id}
                  primary={u.name ?? u.phone ?? u.id.slice(0, 8)}
                  secondary={[
                    u.phone,
                    u.primary_role,
                  ].filter(Boolean).join(" · ")}
                  highlighted={isHighlighted("users", i)}
                  onClick={() => { go(`/users/${u.id}`); }}
                />
              ))}
            </Group>
          )}

          {results.trips.length > 0 && (
            <Group title="Trips" icon={Truck}>
              {results.trips.map((t, i) => (
                <Row
                  key={t.id}
                  primary={t.lane ?? `Trip ${t.id.slice(0, 8)}`}
                  secondary={[
                    t.status.replace(/_/g, " "),
                    t.driver_name,
                    t.shipper_name,
                  ].filter(Boolean).join(" · ")}
                  highlighted={isHighlighted("trips", i)}
                  onClick={() => { go(`/trips/${t.id}`); }}
                />
              ))}
            </Group>
          )}

          {results.loads.length > 0 && (
            <Group title="Loads" icon={Package}>
              {results.loads.map((l, i) => (
                <Row
                  key={l.id}
                  primary={l.lane ?? `Load ${l.id.slice(0, 8)}`}
                  secondary={[
                    l.status.replace(/_/g, " "),
                    l.shipper_name,
                  ].filter(Boolean).join(" · ")}
                  highlighted={isHighlighted("loads", i)}
                  onClick={() => { go(`/loads/${l.id}`); }}
                />
              ))}
            </Group>
          )}

          {results.tickets.length > 0 && (
            <Group title="Tickets" icon={LifeBuoy}>
              {results.tickets.map((t, i) => (
                <Row
                  key={t.id}
                  primary={t.subject}
                  secondary={[
                    t.status.replace(/_/g, " "),
                    t.user_name,
                  ].filter(Boolean).join(" · ")}
                  highlighted={isHighlighted("tickets", i)}
                  onClick={() => { go(`/tickets/${t.id}`); }}
                />
              ))}
            </Group>
          )}

          {results.vehicles.length > 0 && (
            <Group title="Vehicles" icon={ClipboardCheck}>
              {results.vehicles.map((v, i) => (
                <Row
                  key={v.id}
                  primary={v.registration_number ?? v.id.slice(0, 8)}
                  secondary={v.owner_name ?? ""}
                  highlighted={isHighlighted("vehicles", i)}
                  onClick={() => { go(`/vehicles/${v.id}`); }}
                />
              ))}
            </Group>
          )}
        </div>
      )}
    </div>
  );
}

function Group({
  title,
  icon: Icon,
  children,
}: {
  title: string;
  icon: typeof Users;
  children: React.ReactNode;
}): React.JSX.Element {
  return (
    <div className="border-b last:border-b-0">
      <div className="flex items-center gap-1 px-3 py-1.5 text-xs uppercase tracking-wide text-muted-foreground bg-muted/40">
        <Icon className="h-3 w-3" />
        {title}
      </div>
      <ul>{children}</ul>
    </div>
  );
}

function Row({
  primary,
  secondary,
  highlighted = false,
  onClick,
}: {
  primary: string;
  secondary?: string;
  highlighted?: boolean;
  onClick: () => void;
}): React.JSX.Element {
  return (
    <li>
      <button
        type="button"
        onClick={onClick}
        className={cn(
          "w-full text-left px-3 py-2 text-sm hover:bg-muted focus:bg-muted outline-none",
          highlighted && "bg-muted",
        )}
        aria-selected={highlighted}
      >
        <div className="font-medium truncate">{primary}</div>
        {secondary !== undefined && secondary.length > 0 && (
          <div className="text-xs text-muted-foreground truncate">
            {secondary}
          </div>
        )}
      </button>
    </li>
  );
}
